import { useState } from 'react';

import { useTranslation } from '@/i18n/i18n';
import { useHydrated } from '@/hooks/useHydrated';
import { useModuleImpression } from '@/hooks/useModuleImpression';
import { SANCTIONS_START } from '@/components/dowod/ksef-deadline';

/**
 * KSeF statutory-deadline banner (BOO-3240 R3, autoplan decisions #3/#6/#12/#13/#21).
 *
 * Honesty constraints (copy-enforced by guards, keep them in sync):
 * - Dates are STATIC text — no live countdown (no timezone/clock drama).
 * - Sanctions are stated as starting 1 January 2027; the copy must never imply
 *   2026 sanctions (gov.pl: no sanctions in 2026).
 * - Placement is proof-then-pressure: this banner renders only AFTER evidence
 *   content on any page, and never on the homepage at all in this wave.
 *
 * The clock is injected (`now`) so the post-deadline state is testable at the
 * 2026-12-31/2027-01-01 boundary in UTC and Europe/Warsaw. After the sanctions
 * date passes, the banner switches to a neutral "sanctions active" line instead
 * of going stale — the NaN-freeze failure mode is covered by a unit test on
 * SANCTIONS_START validity.
 *
 * Prerendered pages (GEO S7, #9059): the build has no clock a crawler should
 * trust, so until hydration ends the banner states the dates alone
 * (`urgency.sanctionsDate`), which is true before and after 2027-01-01; the
 * mount clock picks the dated variant afterwards.
 */

interface UrgencyBannerProps {
  /** Injected clock for tests; defaults to the real time at render. */
  now?: number;
}

export const UrgencyBanner = ({ now }: UrgencyBannerProps) => {
  const { t } = useTranslation();
  // Lazy initializer keeps the impure clock read out of render (react purity
  // rule); the banner state is static per mount — no ticking needed.
  const [mountNow] = useState(() => Date.now());
  const hydrated = useHydrated();
  const effectiveNow = now ?? (hydrated ? mountNow : null);
  const postDeadline = effectiveNow !== null && effectiveNow >= SANCTIONS_START;

  const impressionRef = useModuleImpression('urgency_banner');

  return (
    <aside
      ref={impressionRef}
      data-slot="urgency-banner"
      aria-labelledby="urgency-banner-kicker"
      className="rounded-lg border border-foreground/20 bg-muted/40 px-5 py-4"
    >
      <p
        id="urgency-banner-kicker"
        className="font-mono text-xs uppercase tracking-widest text-muted-foreground"
      >
        {t('urgency.kicker')}
      </p>
      <p className="mt-2 font-medium text-foreground">
        {postDeadline ? t('urgency.postDeadline') : t('urgency.mandatory')}
      </p>
      {!postDeadline && (
        <p className="mt-1 text-sm text-foreground">
          {effectiveNow === null ? t('urgency.sanctionsDate') : t('urgency.sanctions')}
        </p>
      )}
      {/* Second kicker (judge round 1 B1): the stat lines are context, not
          deadline copy — labeling them separately keeps kicker semantics honest. */}
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
        {t('urgency.contextKicker')}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">{t('urgency.readiness')}</p>
      <p className="mt-1 text-sm text-muted-foreground">{t('urgency.reliability')}</p>
    </aside>
  );
};
